const VERSION_MISMATCH_KEY = 'frontend-version-mismatch';
const STATUS_ENDPOINT = '/api/status';

/**
 * Extract the SHA from the data-manifest-name attribute on the html element.
 * The manifest name typically follows "manifest.<sha>.json", but this parser
 * is intentionally tolerant so polling still works with format variations.
 */
function getManifestSha(): string | null {
  const manifestName = document.documentElement.getAttribute('data-manifest-name');
  if (!manifestName) {
    return null;
  }

  if (!manifestName.startsWith('manifest.')) {
    return null;
  }

  const withoutPrefix = manifestName.slice('manifest.'.length);
  const jsonExtensionIndex = withoutPrefix.indexOf('.json');
  if (jsonExtensionIndex <= 0) {
    return null;
  }

  const hashAndSuffix = withoutPrefix.slice(0, jsonExtensionIndex);
  const hash = hashAndSuffix.split('.', 1)[0];
  return hash.length > 0 ? hash : null;
}

/**
 * Check if the deployed frontend version matches the currently running version.
 * Returns true if there is a mismatch (i.e. a new version is available).
 *
 * Accounts for SHA length differences: the status response may return a full
 * 40-char SHA while the manifest uses a shorter prefix. Uses startsWith
 * comparison, consistent with the pattern in frontendVersionOverrides.ts.
 */
function isVersionMismatch(deployedVersion: string, manifestSha: string): boolean {
  // Normalize to lowercase for comparison
  const deployed = deployedVersion.toLowerCase();
  const manifest = manifestSha.toLowerCase();

  // Check if either is a prefix of the other
  return !deployed.startsWith(manifest) && !manifest.startsWith(deployed);
}

/**
 * Fetch the current deployed frontend version from /api/status.
 * Returns null if the fetch fails or the response is unexpected.
 */
async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const response = await fetch(STATUS_ENDPOINT);
    if (!response.ok) {
      return null;
    }

    const data: unknown = await response.json();
    if (typeof data === 'object' && data !== null && 'frontendVersion' in data) {
      const version = (data as { frontendVersion: unknown }).frontendVersion;
      if (typeof version === 'string' && version.length > 0) {
        return version;
      }
    }

    return null;
  } catch {
    // Silently ignore fetch errors — failed polls must never block normal app operation
    return null;
  }
}

/**
 * Poll the deployed version and set a sessionStorage flag if a mismatch is detected.
 * Returns true if a mismatch was detected, false otherwise.
 */
async function pollVersion(): Promise<boolean> {
  const manifestSha = getManifestSha();
  if (!manifestSha) {
    return false;
  }

  const deployedVersion = await fetchDeployedVersion();
  if (!deployedVersion) {
    return false;
  }

  if (isVersionMismatch(deployedVersion, manifestSha)) {
    sessionStorage.setItem(VERSION_MISMATCH_KEY, 'true');
    return true;
  }

  return false;
}

// Enforce a minimum polling interval of 5 minutes
// Prevents misconfigured flags from causing excessive backend load
const MIN_POLLING_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Start polling /api/status for frontend version changes.
 *
 * The polling service is visibility-aware:
 * - Stops polling when the tab is hidden (zero network requests while backgrounded)
 * - Polls immediately and restarts the interval when the tab becomes visible again
 * - Stops polling entirely once a version mismatch is detected
 *
 * Returns a cleanup function that stops polling and removes event listeners.
 */
export function startVersionPolling({ intervalMs }: { intervalMs: number }): () => void {
  // Guard against malformed flag values like NaN/Infinity causing timer coercion to 0ms.
  const normalizedIntervalMs = Number.isFinite(intervalMs) ? intervalMs : MIN_POLLING_INTERVAL_MS;
  const safeIntervalMs = Math.max(normalizedIntervalMs, MIN_POLLING_INTERVAL_MS);

  let timerId: ReturnType<typeof setInterval> | null = null;
  let stopped = false;

  function stopPolling() {
    if (timerId !== null) {
      clearInterval(timerId);
      timerId = null;
    }
  }

  async function pollAndMaybeStop() {
    const mismatchDetected = await pollVersion();
    if (mismatchDetected) {
      // No point continuing to poll once we know a new version is available.
      // The reload will happen on the next navigation.
      cleanup();
    }
  }

  function startPolling() {
    // Don't start if stopped or a mismatch was already detected
    if (stopped || sessionStorage.getItem(VERSION_MISMATCH_KEY)) {
      return;
    }

    // Ensure start is idempotent in case we get repeated "visible" events
    // without an intervening "hidden" event on some browser lifecycle transitions.
    stopPolling();

    // Defer the first poll to after the polling interval elapses. There's no need
    // to poll immediately (a mismatch is unlikely) and it causes issues (especially
    // in Playwright tests which intentionally set a mismatched version for testing
    timerId = setInterval(() => void pollAndMaybeStop(), safeIntervalMs);
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      stopPolling();
    } else {
      // Tab became visible again — poll immediately and restart the interval.
      // This catches deployments that happened while the tab was in the background.
      startPolling();
    }
  }

  function cleanup() {
    stopped = true;
    stopPolling();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Only start if the tab is currently visible
  if (document.visibilityState !== 'hidden') {
    startPolling();
  }

  return cleanup;
}

/**
 * Check if a version mismatch has been detected and the page should reload.
 * This is intended to be called during navigation interception.
 *
 * Clears the flag before returning true so that after the reload completes,
 * the flag is already gone. This prevents infinite reload loops: if caching
 * prevents the new version from being served, polling will eventually set the
 * flag again, but it's rate-limited by the polling interval (default 1 hour).
 */
export function shouldReloadForNewVersion(): boolean {
  if (sessionStorage.getItem(VERSION_MISMATCH_KEY) !== 'true') {
    return false;
  }

  // Clear before reload — the flag served its purpose.
  clearVersionMismatchFlag();
  return true;
}

/**
 * Clear the version mismatch flag.
 * Called on page load to clear any stale flag from a previous session.
 */
export function clearVersionMismatchFlag(): void {
  sessionStorage.removeItem(VERSION_MISMATCH_KEY);
}
